import { Component, Input, OnInit, OnDestroy, Optional, Self, HostBinding, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BarcodeFormat } from '@zxing/browser';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
    selector: 'app-barcode-edit',
    templateUrl: './barcode-edit.component.html',
    styleUrls: ['./barcode-edit.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: BarcodeEditComponent }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BarcodeEditComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {
  static nextId = 0;
  @HostBinding() id = `app-barcode-edit-${BarcodeEditComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  stateChanges = new Subject<void>();
  focused = false;
  controlType = 'app-barcode-edit';


  @Input()
  get value(): string { return this._value; }
  set value(val: string) {
    this._value = val;
    this.stateChanges.next();
    this.onChange(val);
  }
  private _value = '';

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(dis: boolean) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Optional() @Self() public ngControl: NgControl;

  allowedFormats = [BarcodeFormat.CODE_128, BarcodeFormat.EAN_13, BarcodeFormat.QR_CODE];
  scannerActive = false;
  highlightState = false;

  constructor(@Optional() @Self() ngControl: NgControl, private fm: FocusMonitor, private elementRef: ElementRef) {
    if (ngControl) {
      this.ngControl = ngControl;
      this.ngControl.valueAccessor = this;
    }

    fm.monitor(this.elementRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elementRef.nativeElement);
  }

  get empty() {
    return !this.value;
  }

  get shouldLabelFloat() { return this.focused || !this.empty; }

  get errorState(): boolean {
    return this.ngControl && this.ngControl.invalid && (this.ngControl.dirty || this.ngControl.touched);
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.focus();
    }
  }

  writeValue(value: any): void {
    this.value = value;
    this.stateChanges.next();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  scanBarcode() {
    this.scannerActive = !this.scannerActive;
  }

  handleBarcodeResult(result: string) {
    
 
    this.scannerActive = false;
    this.highlightState = true;
    setTimeout(() => this.highlightState = false, 2000);
    setTimeout(() => {
      this.value = result;
      this.onChange(result);
      this.stateChanges.next();
    }, 1000);
  }

  focus() {
    this.focused = true;
    this.stateChanges.next();
  }

  blur() {
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(input.value);
    this.onTouched();
    this.stateChanges.next();
  }
}